import { memo, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, FileText, FileCode, FileCode2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { getReadableErrorMessage } from "@/shared/lib/sefaz-message";
import {
  downloadBlob,
  openBlankTab,
  openBlobInTab,
} from "@/shared/lib/download";
import { commercialInvoiceService } from "../services/commercial-invoice.service";
import { danfeFilename } from "../utils/danfe";
import type { CommercialInvoiceResponseDTO } from "../types/commercial-invoice.types";

type ActionKind = "open-pdf" | "pdf" | "xml" | "xml-canceled";

interface CommercialInvoiceDownloadActionsProps {
  invoice: CommercialInvoiceResponseDTO;
}

export const CommercialInvoiceDownloadActions = memo(
  function CommercialInvoiceDownloadActions({
    invoice,
  }: CommercialInvoiceDownloadActionsProps) {
    const [busyKind, setBusyKind] = useState<ActionKind | null>(null);

    const reference = invoice.ref ?? String(invoice.id);
    const isAuthorized =
      invoice.status === "ISSUED" || invoice.status === "CANCELED";
    const hasCanceledXml = invoice.status === "CANCELED";

    const run = async (kind: ActionKind, action: () => Promise<void>) => {
      setBusyKind(kind);
      try {
        await action();
      } catch (error: unknown) {
        toast.error(
          getReadableErrorMessage(
            error,
            "Não foi possível obter o documento. Tente novamente.",
          ),
        );
      } finally {
        setBusyKind(null);
      }
    };

    const handleOpenDanfe = () => {
      const tabOpenedWithinUserGesture = openBlankTab();
      return run("open-pdf", async () => {
        try {
          const blob = await commercialInvoiceService.downloadPdf(invoice.id);
          if (!openBlobInTab(blob, tabOpenedWithinUserGesture)) {
            downloadBlob(blob, danfeFilename(blob, reference));
            toast.info(
              "O navegador bloqueou a nova aba — o DANFE foi baixado.",
            );
          }
        } catch (error) {
          tabOpenedWithinUserGesture?.close();
          throw error;
        }
      });
    };

    const isBusy = busyKind !== null;

    if (!isAuthorized) {
      return <span className="text-muted-foreground">—</span>;
    }

    return (
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="icon-sm"
          title="Abrir DANFE em nova aba"
          aria-label="Abrir DANFE da nota comercial em nova aba"
          disabled={isBusy}
          onClick={handleOpenDanfe}
        >
          <ExternalLink className="w-4 h-4" />
        </Button>

        <Button
          variant="ghost"
          size="icon-sm"
          title="Baixar DANFE"
          aria-label="Baixar DANFE da nota comercial"
          disabled={isBusy}
          onClick={() =>
            run("pdf", async () => {
              const blob = await commercialInvoiceService.downloadPdf(
                invoice.id,
              );
              downloadBlob(blob, danfeFilename(blob, reference));
            })
          }
        >
          <FileText className="w-4 h-4" />
        </Button>

        <Button
          variant="ghost"
          size="icon-sm"
          title="Baixar XML"
          aria-label="Baixar XML da nota comercial"
          disabled={isBusy}
          onClick={() =>
            run("xml", async () => {
              const blob = await commercialInvoiceService.downloadXml(
                invoice.id,
              );
              downloadBlob(blob, `nfce-${reference}.xml`);
            })
          }
        >
          <FileCode className="w-4 h-4" />
        </Button>

        <Button
          variant="ghost"
          size="icon-sm"
          title="Baixar XML de cancelamento"
          aria-label="Baixar XML de cancelamento da nota comercial"
          disabled={!hasCanceledXml || isBusy}
          onClick={() =>
            run("xml-canceled", async () => {
              const blob = await commercialInvoiceService.downloadCanceledXml(
                invoice.id,
              );
              downloadBlob(blob, `nfce-cancelada-${reference}.xml`);
            })
          }
        >
          <FileCode2 className="w-4 h-4" />
        </Button>
      </div>
    );
  },
);
